import data from "../../shared/sharedData";

const fieldsToClear = [
  "appASkillsCountryId",
  "appASkillsStateId",
  "fwTerritory",
  "appASkillsSpecial",
];

export default {
  name: "Appendix A.D - Foreign Worker Skills",
  pageInfo: {
    text: "<b><u>Note:</u></b> This section is required if there are special skills, abilities, and/or proficiencies required for the job opportunity. Identify any other relevant specific skills, abilities, and/or proficiencies the foreign worker possesses for the minimum requirements of the job opportunity for which the employer is seeking permanent labor certification. Identify with whom (e.g., employer, school, training provider) and the geographic location (e.g., United States of America, Maryland) the foreign worker attained these skills, abilities, and/or proficiencies. This section will collect two (2) sets of information related to skills, abilities, and/or proficiencies. For electronic filings, if the employer needs to disclose more sections of skills, abilities, and/or proficiencies, the filing system will automatically provide the employer with the option of adding more sets until all required responses are completed. For mailed or paper filings, the employer will make one or more copies of skills, abilities, and proficiencies sets D.a and D.b under this section of the Appendix to complete and attach to the Form ETA-9089. <b><i> Submission of additional skills, abilities, and/or proficiencies in any other form or format will not be accepted.</i></b>",
    coloring: "grey",
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        isVisible: {
          when: "appASkillsInstName",
          is: ["N/A", "n/a", "n/A", "N/a"],
          operator: "compareFieldValuesModal",
          modal: "fwSkillsAppD",
        },
        title: "Section Complete",
        description:
          "You marked N/A, indicating that the foreign worker does not have the relevant U.S. skills, abilities, and proficiencies or foreign equivalent. To add a skill, ability, and/or proficiency requirement, please update 'N/A' to the actual U.S. skills, abilities, and proficiencies or foreign equivalent.",
        type: "info",
      },
    ],
  },
  sections: [
    {
      code: "Appendix A.D",
      sectionHeader: "Skills, Abilities, and Proficiencies",
      machine_name: "fwSkillsAppD",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Skills, Abilities, and Proficiencies",
      modalHeader: "Skills, Abilities, and Proficiencies",
      listType: "table",
      hide: (formApi) => {
        const fieldArray = formApi.getState().values.fwSkillsAppD;
        for (var i in fieldArray) {
          const data = fieldArray[i]?.appASkillsInstName;
          if (
            data === "N/A" ||
            data === "n/a" ||
            data === "n/A" ||
            data === "N/a"
          )
            return true;
        }
      },
      validate: [
        {
          tableRequiredIf: "appendixADRequired",
          message: "Please enter in at least one row.",
          needsFieldValues: true,
          extraData: {
            valueOf: true,
          },
        },
      ],
      editButton: true,
      removeLink: "Remove Skills, Abilities, and Proficiencies",
      maxLength: 250,
      modalFields: [
        {
          code: "1.",
          tableHeader: "Employer/Institution/School/Training Name",
          machine_name: "appASkillsInstName",
          label: "Name of Employer/Institution/School/Training Provider",
          widget: "text",
          validate: [
            {
              isRequired: "appendixADRequired",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: true,
              },
            },
          ],
          parent_machine_name: "add_values_field",
          clearSchemaIfNA: fieldsToClear,
          maxLength: 120,
          tooltip: `1.	Enter the legal business name of the employer or the full name of the institution, school, or training provider from which the foreign worker attained the skills, abilities, and/or proficiencies.  If the foreign worker has no relevant skills, abilities, and/or proficiencies for the minimum requirements of the employer’s job opportunity, enter “N/A.”`,
        },
        {
          code: "1.a.",
          label: "Country",
          machine_name: "appASkillsCountryId",
          widget: "select",
          isInlineModal: true,
          selection: data.country,
          validate: ["required"],
          isVisible: {
            when: "appASkillsInstName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          clearSchema: ["appASkillsStateId", "fwTerritory"],
          tooltip:
            "Enter the country of the employer, institution, school, or training provider where the foreign worker attained the skills, abilities, and/or proficiencies listed in question 1.  If the foreign worker has no relevant skills, abilities, and/or proficiencies for the minimum requirements of the employer’s job opportunity, enter “N/A.”",
        },
        {
          code: "1.b.",
          label: "State, Territory, or Province",
          machine_name: "appASkillsStateId",
          widget: "select",
          selection: data.state,
          state: true,
          tableHeader: "State",
          maxLength: 35,
          validate: ["required"],
          isVisible: {
            when: (formApi) => {
              const countryCheck = ["UNITED STATES OF AMERICA"];
              const country =
                formApi.getState().values.add_values_field?.[0]
                  ?.appASkillsCountryId;
              const neededValue =
                formApi.getState().values.add_values_field?.[0]
                  ?.appASkillsInstName;
              if (!!neededValue && countryCheck.includes(country)) {
                return true;
              }
              return false;
            },
            is: "fwStateSelectCheck",
            operator: "customValidation",
          },
          tooltip:
            "Enter the state, territory, or province of the employer, institution, school, or training provider from which foreign worker attained the skills, abilities, and/or proficiencies listed in question 1.  If the foreign worker has no relevant skills, abilities, and/or proficiencies for the minimum requirements of the employer’s job opportunity, enter “N/A.”",
        },
        {
          code: "1.b.",
          machine_name: "fwTerritory",
          label: "State, Territory, or Province",
          tableHeader: "Territory/Province",
          widget: "text",
          validate: [
            "required",
            {
              minLength: 2,
              message: "State must contain at least 2 characters",
            },
          ],
          isVisible: {
            when: (formApi) => {
              const countryCheck = [
                "UNITED STATES OF AMERICA",
                "- Select -",
                null,
                undefined,
              ];
              const country =
                formApi.getState().values.add_values_field?.[0]
                  ?.appASkillsCountryId;
              const neededValue =
                formApi.getState().values.add_values_field?.[0]
                  ?.appASkillsInstName;
              if (!!neededValue && !countryCheck.includes(country)) {
                return true;
              }
              return false;
            },
            is: "fwStateTextCheck",
            operator: "customValidation",
          },
          maxLength: 120,
          tooltip: `Enter the state, territory, or province of the employer, institution, school, or training provider from which foreign worker attained the skills, abilities, and/or proficiencies listed in question 1.  If the foreign worker has no relevant skills, abilities, and/or proficiencies for the minimum requirements of the employer’s job opportunity, enter “N/A.”`,
        },
        {
          code: "1.c.",
          machine_name: "appASkillsSpecial",
          hideOnTable: true,
          label:
            "Description of specific skills, abilities, and/or proficiencies the foreign worker possesses or attained, which help establish whether the foreign worker meets the requirements identified for the job opportunity. (up to 1,500 characters)",
          tooltip:
            "In the space provided (up to 1,500 characters), enter a brief description of specific skills, abilities, and/or proficiencies the foreign worker attained for the minimum requirements of the employer’s job opportunity.  If the foreign worker has no relevant skills, abilities, and/or proficiencies for the minimum requirements of the employer’s job opportunity, enter “N/A.”",
          widget: "text_area",
          validate: [
            "required",
            {
              minLength: 5,
            },
          ],
          isVisible: {
            when: "appASkillsInstName",
            is: "useless", // useless field, needed to fire isVisible cond
            operator: "has_value_and_not_na",
          },
          inputLimit: 1500,
          width: 8,
          rows: 8,
        },
      ],
    },
  ],
};
